<template>
    <widget-card
        :icon="QrCode"
        :info-tooltip="trans('dashboard.v2.mean_time_between_critical_failure.info')"
        :loading="loading"
        :title="trans('dashboard.v2.mean_time_between_critical_failure.title')"
    >
        <template v-if="lastUpdated" #header-subtitle>
            <div class="small mt-1 text-muted">{{ trans('dashboard.v2.last_update') }}: {{ lastUpdated }}</div>
        </template>
        <template #header-end>
            <jet-button v-if="debug" class="btn-icon" variant="secondary" @click="showDebugInfo = !showDebugInfo">
                <icon name="cil-bug" />
            </jet-button>
        </template>
        <div class="h-100">
            <div class="d-flex flex-column flex-md-row align-items-stretch h-100">
                <chart-loading-skeleton v-if="loading" />
                <empty-widget-state v-else-if="!hasData" :text="trans('dashboard.v2.mean_time_between_critical_failure.empty_state')" />
                <template v-else>
                    <c-chart
                        :key="componentKey"
                        :custom-tooltips="false"
                        :data="chartData"
                        :options="chartOptions"
                        :plugins="[ChartDataLabels]"
                        class="grow"
                        type="bar"
                    />
                    <chart-legend :legend-items="legendItems" :loading="loading" />
                </template>
            </div>
        </div>
    </widget-card>
    <debug-view v-if="debug" v-model="showDebugInfo" title="Mean Time Between Critical Failure<br/><small>Debug Info</small>">
        <pre style="background-color: #fefefe">{{ debugInfo }}</pre>
    </debug-view>
</template>

<script setup>
import { computed, toRef } from 'vue';
import WidgetCard from '@/Pages/DashboardV2/Components/WidgetCard.vue';
import ChartLoadingSkeleton from '@/Pages/DashboardV2/Components/ChartLoadingSkeleton.vue';
import ChartLegend from '@/Pages/DashboardV2/Components/ChartLegend.vue';
import EmptyWidgetState from '@/Pages/DashboardV2/Components/EmptyWidgetState.vue';
import { CChart } from '@coreui/vue-chartjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { trans } from '@/mixins/i18n';
import QrCode from '../../../../icons/QrCode.vue';
import DebugView from '@/Components/DebugView.vue';
import JetButton from '@/Jetstream/Button.vue';
import { useChartWidget } from '@/composables/useChartWidget';
import { getBaseChartOptions } from '@/Utils/chartDefaults';

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
    availableGroupings: {
        type: Array,
        default: () => [],
    },
});

const { data, loading, componentKey, hasData, lastUpdated, selectedGrouping, groupingOptions, debug, showDebugInfo, debugInfo } = useChartWidget({
    dataUrl: computed(() => props.widgetData?.dataUrl),
    filters: toRef(props, 'filters'),
    groupingOptions: computed(() => props.widgetData?.groupingOptions?.data ?? []),
    availableGroupings: toRef(props, 'availableGroupings'),
    parseResponse: (response) => ({
        data: response.data.data,
        lastUpdated: response.data.lastUpdated,
        debug: response.data.debug,
    }),
    defaultGrouping: 'type',
});

// Extract data from consolidated format
const labels = computed(() => data.value?.map((item) => item.label) || []);
const values = computed(() => data.value?.map((item) => item.mtbcfHours) || []);

const legendItems = computed(() => [
    {
        key: 'actual',
        text: trans('dashboard.v2.mean_time_between_critical_failure.actual_mtbcf'),
        color: '#2c54ea',
        shape: 'square',
        placeholderWidth: '120px',
    },
]);

const chartData = computed(() => {
    if (!hasData.value) {
        return {
            labels: [],
            datasets: [],
        };
    }

    const topValue = Math.ceil(Math.max(...values.value) + 2);

    return {
        labels: data.value.map((l) => l.assetType),
        datasets: [
            {
                type: 'bar',
                label: trans('dashboard.v2.mean_time_between_critical_failure.actual_mtbcf'),
                backgroundColor: '#2C54EA',
                borderRadius: 4,
                data: values.value,
                barPercentage: 0.6,
                datalabels: {
                    display: false,
                },
            },
            // Transparent scatter dataset to host the datalabels above each column
            {
                type: 'scatter',
                label: 'labels',
                order: 2,
                data: labels.value.map((_, idx) => ({ x: idx, y: topValue })),
                pointRadius: 0,
                pointHoverRadius: 0,
                datalabels: {
                    display: 'auto',
                    direction: 'horizontal',
                    clamp: true,
                    anchor: 'end', // position relative to the point (chart top)
                    align: 'end',
                    offset: 4,
                    backgroundColor: '#9AAEF9',
                    borderRadius: 2,
                    color: '#2C54EA',
                    font: {
                        size: 10,
                        weight: 'bold',
                    },
                    formatter: (value, ctx) => {
                        const raw = values.value[ctx.dataIndex];
                        const hours = Math.floor(raw);
                        const minutes = Math.round((raw - hours) * 60)
                            .toString()
                            .padStart(2, '0');
                        return `${hours}:${minutes}h`;
                    },
                    padding: {
                        top: 2,
                        bottom: 2,
                        left: 4,
                        right: 4,
                    },
                },
            },
        ],
    };
});

const chartOptions = computed(() => {
    const maxValue = Math.max(...(values.value.length ? values.value : [10]));
    return {
        ...getBaseChartOptions(),
        animation: false,
        plugins: {
            ...getBaseChartOptions().plugins,
            tooltip: {
                enabled: true,
                callbacks: {
                    title: (context) => data.value[context[0].dataIndex]?.label,
                    label: (context) => `${context.parsed.y} h`,
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    autoSkip: true,
                    maxRotation: 45,
                    minRotation: 0,
                    font: {
                        size: 11,
                    },
                },
            },
            y: {
                beginAtZero: true,
                grace: '5%',
                max: Math.ceil(maxValue + 2),
                grid: {
                    drawBorder: false,
                },
                ticks: {
                    callback: (value) => `${value}h`,
                    autoSkip: true,
                },
            },
        },
        layout: {
            padding: {
                top: 40,
            },
        },
    };
});
</script>
